/**
 * Pronod collection filters
 * - Checkbox/price changes wait for SHOW RESULTS on desktop
 * - Dual price range slider (theme green)
 * - Same Ajax section render flow as Dawn FacetFiltersForm
 */

function facetsDebounce(fn, wait) {
  let timer;
  return function debounced() {
    const args = arguments;
    const context = this;
    clearTimeout(timer);
    timer = setTimeout(function () {
      fn.apply(context, args);
    }, wait);
  };
}

function facetsRawNumber(value) {
  if (value == null || value === '') return NaN;
  return Number(String(value).replace(/[^\d.]/g, ''));
}

function facetsPlainAmount(value) {
  var n = facetsRawNumber(value);
  if (!Number.isFinite(n)) n = 0;
  return String(Math.round(n));
}

class FacetFiltersForm extends HTMLElement {
  constructor() {
    super();
    this.onActiveFilterClick = this.onActiveFilterClick.bind(this);
    this.debouncedOnSubmit = facetsDebounce(this.onSubmitHandler.bind(this), 500);
    this._boundShowResults = this.onShowResultsClick.bind(this);
  }

  connectedCallback() {
    if (this.dataset.facetsBound === 'true') return;
    this.dataset.facetsBound = 'true';

    var form = this.querySelector('form');
    if (!form) return;

    form.addEventListener('input', this.onFormInput.bind(this));
    form.addEventListener('submit', this.onFormSubmit.bind(this));

    this.querySelectorAll('[data-facts-show-results]').forEach(
      function (btn) {
        btn.addEventListener('click', this._boundShowResults);
      }.bind(this)
    );
  }

  onFormInput(event) {
    var form = event.currentTarget;
    // Desktop filter form: do not auto-filter. Wait for SHOW RESULTS.
    if (form.id === 'FacetFiltersForm' && form.getAttribute('data-deferred') === 'true') {
      return;
    }
    // Range sliders never auto-submit.
    if (event.target && event.target.matches('[data-facts-range-min], [data-facts-range-max]')) {
      return;
    }
    this.debouncedOnSubmit(event);
  }

  onFormSubmit(event) {
    event.preventDefault();
    this.onSubmitHandler(event);
  }

  onShowResultsClick(event) {
    event.preventDefault();
    event.stopPropagation();
    this.onSubmitHandler(event);
  }

  static setListeners() {
    window.addEventListener('popstate', function (event) {
      var searchParams = event.state ? event.state.searchParams : FacetFiltersForm.searchParamsInitial;
      if (searchParams === FacetFiltersForm.searchParamsPrev) return;
      FacetFiltersForm.renderPage(searchParams, null, false);
    });

    // Backup: works even if custom element upgrade is delayed
    document.addEventListener('click', function (event) {
      var btn = event.target.closest('[data-facts-show-results]');
      if (!btn) return;
      var host = btn.closest('facet-filters-form');
      if (host && typeof host.onShowResultsClick === 'function') return; // host handler will run
      event.preventDefault();
      FacetFiltersForm.submitFromButton(btn, event);
    });
  }

  static submitFromButton(btn, event) {
    var form = btn.closest('form');
    var isMobile = form && form.id === 'FacetFiltersFormMobile';
    var params = FacetFiltersForm.buildSearchParams(isMobile);
    FacetFiltersForm.renderPage(params, event, true);
  }

  static buildSearchParams(isMobile) {
    var parts = [];
    if (isMobile) {
      var mobileForm = document.getElementById('FacetFiltersFormMobile');
      if (mobileForm) parts.push(new URLSearchParams(new FormData(mobileForm)).toString());
    } else {
      ['FacetSortForm', 'FacetFiltersForm', 'FacetSortDrawerForm'].forEach(function (id) {
        var form = document.getElementById(id);
        if (!form) return;
        parts.push(new URLSearchParams(new FormData(form)).toString());
      });
    }
    return parts.filter(Boolean).join('&');
  }

  static toggleActiveFacets(disable) {
    document.querySelectorAll('.js-facet-remove').forEach(function (element) {
      element.classList.toggle('disabled', disable !== false);
    });
  }

  static getSectionId() {
    var grid = document.getElementById('product-grid');
    if (grid && grid.dataset.id) return grid.dataset.id;
    var filters = document.getElementById('main-collection-filters');
    if (filters && filters.dataset.id) return filters.dataset.id;
    var section = document.querySelector('#ProductGridContainer')?.closest('[id^="shopify-section-"]');
    if (section) return section.id.replace('shopify-section-', '');
    return null;
  }

  static renderPage(searchParams, event, updateURLHash) {
    var sectionId = FacetFiltersForm.getSectionId();
    if (!sectionId) {
      console.warn('[facets] Missing section id for product grid');
      return;
    }

    FacetFiltersForm.searchParamsPrev = searchParams;
    var countContainer = document.getElementById('ProductCount');
    var countContainerDesktop = document.getElementById('ProductCountDesktop');
    var collectionEl = document.querySelector('#ProductGridContainer .collection, #ProductGridContainer .dashcam-collection');
    if (collectionEl) collectionEl.classList.add('loading');
    if (countContainer) countContainer.classList.add('loading');
    if (countContainerDesktop) countContainerDesktop.classList.add('loading');

    var url = window.location.pathname + '?section_id=' + sectionId + (searchParams ? '&' + searchParams : '');
    var filterDataUrl = function (element) {
      return element.url === url;
    };

    if (FacetFiltersForm.filterData.some(filterDataUrl)) {
      FacetFiltersForm.renderSectionFromCache(filterDataUrl, event);
    } else {
      FacetFiltersForm.renderSectionFromFetch(url, event);
    }

    if (updateURLHash !== false) FacetFiltersForm.updateURLHash(searchParams || '');
  }

  static renderSectionFromFetch(url, event) {
    fetch(url)
      .then(function (response) {
        return response.text();
      })
      .then(function (html) {
        FacetFiltersForm.filterData = FacetFiltersForm.filterData.concat([{ html: html, url: url }]);
        FacetFiltersForm.renderFilters(html, event);
        FacetFiltersForm.renderProductGridContainer(html);
        FacetFiltersForm.renderProductCount(html);
      })
      .catch(function (error) {
        console.error('[facets] filter fetch failed', error);
      });
  }

  static renderSectionFromCache(filterDataUrl, event) {
    var html = FacetFiltersForm.filterData.find(filterDataUrl).html;
    FacetFiltersForm.renderFilters(html, event);
    FacetFiltersForm.renderProductGridContainer(html);
    FacetFiltersForm.renderProductCount(html);
  }

  static renderProductGridContainer(html) {
    var source = new DOMParser().parseFromString(html, 'text/html').getElementById('ProductGridContainer');
    var target = document.getElementById('ProductGridContainer');
    if (!source || !target) return;
    target.innerHTML = source.innerHTML;
    target.querySelectorAll('.scroll-trigger').forEach(function (element) {
      element.classList.add('scroll-trigger--cancel');
    });
  }

  static renderProductCount(html) {
    var parsed = new DOMParser().parseFromString(html, 'text/html');
    var sourceCount = parsed.getElementById('ProductCount');
    if (!sourceCount) return;
    var container = document.getElementById('ProductCount');
    var containerDesktop = document.getElementById('ProductCountDesktop');
    if (container) {
      container.innerHTML = sourceCount.innerHTML;
      container.classList.remove('loading');
    }
    if (containerDesktop) {
      containerDesktop.innerHTML = sourceCount.innerHTML;
      containerDesktop.classList.remove('loading');
    }
  }

  static renderFilters(html, event) {
    var parsedHTML = new DOMParser().parseFromString(html, 'text/html');

    // Replace whole forms so layout stays identical before/after filter
    var newDesktop = parsedHTML.getElementById('FacetFiltersForm');
    var oldDesktop = document.getElementById('FacetFiltersForm');
    if (newDesktop && oldDesktop) {
      oldDesktop.innerHTML = newDesktop.innerHTML;
    }

    var newMobile = parsedHTML.getElementById('FacetFiltersFormMobile');
    var oldMobile = document.getElementById('FacetFiltersFormMobile');
    if (newMobile && oldMobile) {
      oldMobile.innerHTML = newMobile.innerHTML;
    }

    FacetFiltersForm.renderActiveFacets(parsedHTML);

    document.querySelectorAll('price-range').forEach(function (el) {
      if (typeof el.reinit === 'function') el.reinit();
    });

    document.querySelectorAll('facet-filters-form').forEach(function (el) {
      el.dataset.facetsBound = '';
      if (typeof el.connectedCallback === 'function') el.connectedCallback();
    });
  }

  static renderActiveFacets(html) {
    ['.active-facets-mobile', '.active-facets-desktop'].forEach(function (selector) {
      var next = html.querySelector(selector);
      var current = document.querySelector(selector);
      if (!next || !current) return;
      current.innerHTML = next.innerHTML;
    });
    FacetFiltersForm.toggleActiveFacets(false);
  }

  static updateURLHash(searchParams) {
    history.pushState(
      { searchParams: searchParams },
      '',
      window.location.pathname + (searchParams ? '?' + searchParams : '')
    );
  }

  createSearchParams(form) {
    return new URLSearchParams(new FormData(form)).toString();
  }

  onSubmitForm(searchParams, event) {
    FacetFiltersForm.renderPage(searchParams, event, true);
  }

  onSubmitHandler(event) {
    if (event && event.preventDefault) event.preventDefault();

    var sourceForm =
      (event && event.target && event.target.closest && event.target.closest('form')) ||
      this.querySelector('form');
    var isMobile = !!(sourceForm && sourceForm.id === 'FacetFiltersFormMobile');
    var searchParams = FacetFiltersForm.buildSearchParams(isMobile);
    this.onSubmitForm(searchParams, event);
  }

  onActiveFilterClick(event) {
    event.preventDefault();
    FacetFiltersForm.toggleActiveFacets(true);
    var href = event.currentTarget.href || '';
    var url = href.indexOf('?') === -1 ? '' : href.slice(href.indexOf('?') + 1);
    FacetFiltersForm.renderPage(url, event, true);
  }
}

FacetFiltersForm.filterData = [];
FacetFiltersForm.searchParamsInitial = window.location.search.slice(1);
FacetFiltersForm.searchParamsPrev = window.location.search.slice(1);

try {
  if (!customElements.get('facet-filters-form')) {
    customElements.define('facet-filters-form', FacetFiltersForm);
  }
} catch (e) {}
FacetFiltersForm.setListeners();

class PriceRange extends HTMLElement {
  connectedCallback() {
    this.reinit();
  }

  reinit() {
    this.minText = this.querySelector('[data-facts-min-input]');
    this.maxText = this.querySelector('[data-facts-max-input]');
    this.minRange = this.querySelector('[data-facts-range-min]');
    this.maxRange = this.querySelector('[data-facts-range-max]');
    this.rangeFill = this.querySelector('[data-facts-range]');
    this.bubbleMin = this.querySelector('[data-facts-bubble-min]');
    this.bubbleMax = this.querySelector('[data-facts-bubble-max]');
    this.display = this.querySelector('[data-facts-price-display]');
    this.currency = this.dataset.currency || '₹';

    this.max = facetsRawNumber(this.dataset.rangeMax);
    if (!Number.isFinite(this.max) || this.max <= 0) {
      this.max = facetsRawNumber(this.maxRange && this.maxRange.max);
    }
    if (!Number.isFinite(this.max) || this.max <= 0) this.max = 0;

    if (this._priceBound) return;
    this._priceBound = true;

    this.querySelectorAll('.field__input').forEach(
      function (element) {
        element.addEventListener('change', this.onRangeChange.bind(this));
        element.addEventListener('keydown', this.onKeyDown.bind(this));
      }.bind(this)
    );

    if (this.minRange && this.maxRange) {
      this.minRange.addEventListener(
        'input',
        function () {
          this.onSliderInput('min');
        }.bind(this)
      );
      this.maxRange.addEventListener(
        'input',
        function () {
          this.onSliderInput('max');
        }.bind(this)
      );
    }

    this.syncFromTextInputs();
    this.setMinAndMaxValues();
    this.updateVisuals();
  }

  onSliderInput(which) {
    if (!this.minRange || !this.maxRange) return;
    var minVal = Number(this.minRange.value);
    var maxVal = Number(this.maxRange.value);

    if (minVal > maxVal) {
      if (which === 'min') minVal = maxVal;
      else maxVal = minVal;
      this.minRange.value = String(minVal);
      this.maxRange.value = String(maxVal);
    }

    if (this.minText) this.minText.value = minVal <= 0 ? '' : facetsPlainAmount(minVal);
    if (this.maxText) this.maxText.value = maxVal >= this.max ? '' : facetsPlainAmount(maxVal);

    this.setMinAndMaxValues();
    this.updateVisuals();
  }

  syncFromTextInputs() {
    if (!this.minRange || !this.maxRange) return;
    var minVal = facetsRawNumber(this.minText && this.minText.value);
    var maxVal = facetsRawNumber(this.maxText && this.maxText.value);
    this.minRange.value = String(Number.isFinite(minVal) ? minVal : 0);
    this.maxRange.value = String(Number.isFinite(maxVal) ? maxVal : this.max);
  }

  updateVisuals() {
    if (!this.minRange || !this.maxRange || !this.max) return;

    var minVal = Number(this.minRange.value) || 0;
    var maxVal = Number(this.maxRange.value);
    if (!Number.isFinite(maxVal)) maxVal = this.max;

    var minPct = Math.max(0, Math.min(100, (minVal / this.max) * 100));
    var maxPct = Math.max(0, Math.min(100, (maxVal / this.max) * 100));

    if (this.rangeFill) {
      var fillWidth = Math.max(0, maxPct - minPct);
      this.rangeFill.setAttribute(
        'style',
        'left:' +
          minPct +
          '%;width:' +
          fillWidth +
          '%;background:#6cbd45 !important;background-color:#6cbd45 !important;height:100%;top:0;bottom:0;position:absolute;border-radius:999px;z-index:2;display:block;'
      );
    }

    if (this.bubbleMin) {
      this.bubbleMin.textContent = this.currency + facetsPlainAmount(minVal);
      this.bubbleMin.setAttribute(
        'style',
        'left:' + minPct + '%;background:#6cbd45 !important;background-color:#6cbd45 !important;'
      );
    }
    if (this.bubbleMax) {
      this.bubbleMax.textContent = this.currency + facetsPlainAmount(maxVal);
      this.bubbleMax.setAttribute(
        'style',
        'left:' + maxPct + '%;background:#6cbd45 !important;background-color:#6cbd45 !important;'
      );
    }
    if (this.display) {
      this.display.textContent =
        this.currency + facetsPlainAmount(minVal) + ' - ' + this.currency + facetsPlainAmount(maxVal);
    }
  }

  onRangeChange(event) {
    this.adjustToValidValues(event.currentTarget);
    this.setMinAndMaxValues();
    this.syncFromTextInputs();
    this.updateVisuals();
  }

  onKeyDown(event) {
    if (event.metaKey) return;
    var pattern = /[0-9]|\.|,|'| |Tab|Backspace|Enter|ArrowUp|ArrowDown|ArrowLeft|ArrowRight|Delete|Escape/;
    if (!String(event.key).match(pattern)) event.preventDefault();
  }

  setMinAndMaxValues() {
    var inputs = this.querySelectorAll('.field__input');
    var minInput = inputs[0];
    var maxInput = inputs[1];
    if (!minInput || !maxInput) return;
    if (maxInput.value) minInput.setAttribute('data-max', maxInput.value);
    if (minInput.value) maxInput.setAttribute('data-min', minInput.value);
    if (minInput.value === '') maxInput.setAttribute('data-min', '0');
    if (maxInput.value === '') minInput.setAttribute('data-max', maxInput.getAttribute('data-max'));
  }

  adjustToValidValues(input) {
    var value = facetsRawNumber(input.value);
    var min = facetsRawNumber(input.getAttribute('data-min'));
    var max = facetsRawNumber(input.getAttribute('data-max'));
    if (!Number.isFinite(value)) return;
    if (Number.isFinite(min) && value < min) input.value = facetsPlainAmount(min);
    if (Number.isFinite(max) && value > max) input.value = facetsPlainAmount(max);
  }
}

try {
  if (!customElements.get('price-range')) {
    customElements.define('price-range', PriceRange);
  }
} catch (e) {}

class FacetRemove extends HTMLElement {
  constructor() {
    super();
    var facetLink = this.querySelector('a');
    if (!facetLink) return;
    facetLink.setAttribute('role', 'button');
    facetLink.addEventListener(
      'click',
      function (event) {
        this.closeFilter(event);
      }.bind(this)
    );
  }

  closeFilter(event) {
    event.preventDefault();
    var form = this.closest('facet-filters-form') || document.querySelector('facet-filters-form');
    if (form && typeof form.onActiveFilterClick === 'function') {
      form.onActiveFilterClick(event);
    } else {
      var href = event.currentTarget.href || '';
      var url = href.indexOf('?') === -1 ? '' : href.slice(href.indexOf('?') + 1);
      FacetFiltersForm.renderPage(url, event, true);
    }
  }
}

try {
  if (!customElements.get('facet-remove')) {
    customElements.define('facet-remove', FacetRemove);
  }
} catch (e) {}

if (!customElements.get('menu-drawer')) {
  customElements.define(
    'menu-drawer',
    class extends HTMLElement {
      bindEvents() {}
    }
  );
}

/* Mobile filter drawer: body lock + backdrop close */
(function initFactsMobileDrawer() {
  function syncBodyLock() {
    var openDrawer = document.querySelector('.facts-mobile .mobile-facets__disclosure[open]');
    document.body.classList.toggle('facts-mobile-open', !!openDrawer);
  }

  document.addEventListener('toggle', function (event) {
    var details = event.target;
    if (!details || !details.classList || !details.classList.contains('mobile-facets__disclosure')) return;
    syncBodyLock();
  }, true);

  document.addEventListener('click', function (event) {
    var details = event.target.closest && event.target.closest('.facts-mobile .mobile-facets__disclosure[open]');
    if (!details) return;

    // Click on backdrop (::before is on summary's parent - use summary click outside open button)
    // Close when clicking the dimmed area: the summary::before covers viewport; summary also contains open button.
    // If click is on the disclosure summary but NOT on the open control inside closed state...
    // Better: close button already works. For backdrop, Dawn uses summary::before.
    // Clicks on ::before hit the summary element.
    var summary = event.target.closest('summary.mobile-facets__open-wrapper, summary.mobile-facts__open-wrapper, .mobile-facets__disclosure > summary');
    if (summary && details.open && summary.parentElement === details) {
      // Only close if click is on the overlay portion — approximate: if target is summary itself
      // and not the open label when closed. When open, summary is still in DOM at top.
      // Actually when open, clicking summary toggles closed in native details - good.
    }
  });

  document.addEventListener('click', function (event) {
    var btn = event.target.closest('[data-facts-show-results]');
    if (!btn) return;
    var mobileDetails = btn.closest('.mobile-facets__disclosure');
    if (mobileDetails) {
      // let submit handler run first via bubble, then close
      setTimeout(function () {
        mobileDetails.removeAttribute('open');
        document.body.classList.remove('facts-mobile-open');
      }, 0);
    }
  });
})();
